import { Anchor, Button, Group, Stack, Text, TextInput, Title } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";

import { isApiError } from "../../api/errors";
import {
  getListStaffTeamsQueryKey,
  useCreateStaffTeam,
} from "../../api/generated/endpoints/staff/staff";
import { getListMyTeamsQueryKey } from "../../api/generated/endpoints/teams/teams";
import { StaffTeamCreate, staffTeamCreateNameMax } from "../../api/generated/zod";
import { refreshAccessScope } from "../../api/session";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { useIdempotencyKey } from "../../platform/idempotency";

const NAME_PROBLEM = `Enter a name of up to ${staffTeamCreateNameMax} characters.`;

/**
 * A team for an owner (A7): staff holds it until the owner accepts the owner invitation the
 * API emails, which is how a team is born while sign-up is invite-only (D1).
 */
export function CreateTeamForm() {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  // The fields the generated schema refused, each shown on its input.
  const [invalid, setInvalid] = useState<ReadonlySet<string>>(new Set());
  const idempotency = useIdempotencyKey();
  // The team is saved whether or not its owner's email went: `EMAIL_UNAVAILABLE` comes after the
  // commit (spec platform/email-resend, decision 12). Listing it and clearing the form leaves a
  // resend from the team, never a second team under a new key. The staff user owns the team,
  // for good with their own email, else until the named owner accepts, so their scope and teams
  // are read again: staff in no team are then in it, and the page keeps its frame meanwhile
  // (`LastTeamFrame`), so what this form shows stays (spec ui-perf/staff-no-team-admin-panel).
  const saved = async () => {
    setName("");
    setOwnerEmail("");
    await Promise.all([
      refreshAccessScope(queryClient),
      queryClient.invalidateQueries({ queryKey: getListMyTeamsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getListStaffTeamsQueryKey() }),
    ]);
  };
  const create = useCreateStaffTeam({
    mutation: {
      onSuccess: saved,
      onError: async (error) => {
        if (isApiError(error, "EMAIL_UNAVAILABLE")) await saved();
      },
    },
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = StaffTeamCreate.safeParse({ name: name.trim(), owner_email: ownerEmail.trim() });
    const refused = parsed.success ? [] : parsed.error.issues.map((each) => String(each.path[0]));
    setInvalid(new Set(refused));
    if (!parsed.success) return;
    const key = idempotency.keyFor(parsed.data);
    create.mutate(
      { data: parsed.data, headers: { "Idempotency-Key": key } },
      { onError: (error) => idempotency.failed(key, parsed.data, error) },
    );
  };
  return (
    <form onSubmit={submit} noValidate>
      <Stack gap="xs" maw={520}>
        <Title order={2} size="h4">
          Create a team for an owner
        </Title>
        <Group grow align="flex-start">
          <TextInput
            label="Team name"
            value={name}
            error={invalid.has("name") ? NAME_PROBLEM : undefined}
            onChange={(event) => setName(event.currentTarget.value)}
          />
          <TextInput
            label="Owner's email"
            type="email"
            value={ownerEmail}
            error={invalid.has("owner_email") ? "Enter the owner's email address." : undefined}
            onChange={(event) => setOwnerEmail(event.currentTarget.value)}
          />
        </Group>
        {create.isError ? <ErrorAlert error={create.error} /> : null}
        {create.isSuccess ? (
          <Text component="output" size="sm">
            Created{" "}
            <Anchor
              renderRoot={(props) => (
                <Link
                  {...props}
                  to="/staff/teams/$teamId"
                  params={{ teamId: create.data.team.id }}
                />
              )}
            >
              {create.data.team.name}
            </Anchor>
            {create.data.invitation === null || create.data.invitation === undefined
              ? "."
              : `; the owner invitation went to ${create.data.invitation.email}.`}
          </Text>
        ) : null}
        <Group>
          <Button type="submit" loading={create.isPending}>
            Create team
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
